import { HTMLAttributes } from 'react';
import { clsx } from 'clsx';

interface ProgressProps extends HTMLAttributes<HTMLDivElement> {
  value: number; // 0-100
  max?: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export function Progress({
  value,
  max = 100,
  size = 'md',
  showLabel = false,
  className,
  ...props
}: ProgressProps) {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  const sizes = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-3',
  };

  return (
    <div className={clsx('w-full', className)} {...props}>
      <div
        className={clsx(
          'w-full rounded-full bg-dark-100 overflow-hidden',
          sizes[size]
        )}
      >
        <div
          className="h-full bg-gradient-to-r from-primary-600 to-primary-400 transition-all duration-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]"
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showLabel && (
        <div className="mt-1 text-xs text-primary-300 text-right">
          {Math.round(percentage)}%
        </div>
      )}
    </div>
  );
}

interface StarRatingProps {
  stars: 0 | 1 | 2 | 3;
  maxStars?: number;
  size?: 'sm' | 'md' | 'lg';
}

export function StarRating({ stars, maxStars = 3, size = 'md' }: StarRatingProps) {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  return (
    <div className="flex gap-1">
      {Array.from({ length: maxStars }).map((_, index) => (
        <svg
          key={index}
          className={clsx(
            sizes[size],
            index < stars
              ? 'text-primary-500 fill-primary-500'
              : 'text-dark-200 fill-none',
            'transition-all duration-200'
          )}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
          />
        </svg>
      ))}
    </div>
  );
}
